/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */
import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../../common/constants';
import utils from '../../../common/utils';

export default class ChatutilityAddBookService extends BaseService {
  constructor() {
    super(constants.CHATUTILITY_ADD_BOOK_DATA, 'chatutilityaddBookStatus');
    this.tenantName = null;
  }

  handleEvent(eventName, payload) {
    const { addBookData, tenantName } = payload;
    this.tenantName = tenantName;
    const tenantId = addBookData?.tenantId;
    if (!tenantId) {
      const notification = {
        open: true,
        message: 'Tenant ID is required to add an identifier. Please refresh the page and try again.',
        type: 'error'
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      return Promise.reject(new Error('Tenant ID is required'));
    }

    const headers = { [constants.BERLIN_TENANT_ID_HEADER]: tenantId };
    const { tenantId: _, ...identifierDataToSend } = addBookData;
    // Convert identifierId to bookId for API (API expects bookId)
    const apiData = { ...identifierDataToSend };
    if (apiData.identifierId) {
      apiData.bookId = apiData.identifierId;
      delete apiData.identifierId;
    }
    return this.getClient('aiadmin').post('/chatutility/titles/add', apiData, { headers });
  }

  handleResponse(response) {
    const { data } = response;
    // Handle both wrapped and direct response formats
    const bookData = data?.data || data;
    Framework.getStoreRegistry()
      .getStore('chatutilitybooksState')
      .setAddedBookData(Array.isArray(bookData) ? bookData : [bookData]);
    if (data?.status === 'success' || response.status === 200 || response.status === 201) {
      const notification = {
        open: true,
        type: 'success',
        message: 'Identifier added successfully'
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
    }
    Framework.redirectTo(`/aiadmin/${this.tenantName}/identifiers`);
  }

  handleError(error) {
    const { response } = error;
    if (response) {
      const { status, data } = response;
      if (status === 401) {
        utils.redirectToLogin();
      }
      if (status === 500 && data && data.error) {
        const notification = {
          open: true,
          message: data.error.message || 'Failed to add identifier',
          type: 'error'
        };
        Framework.getStoreRegistry().getStore('notificationState').set(notification);
      }
    }
    Framework.redirectTo(`/aiadmin/${this.tenantName}/identifiers`);
  }
}
